import { LoadingOverlay } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { TaskDetails } from 'src/Components/Tasks/TaskDetails';
import useDeleteTask from 'src/Hooks/Mutations/useDeleteTask';
import useUpdateTask from 'src/Hooks/Mutations/useUpdateTask';
import useFetchTasks from 'src/Hooks/Queries/useFetchTasks';
import { TaskStatus } from 'src/types/task.types';

type TaskParams = {
  assetId: string;
  taskId: string;
};

const ViewTaskDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assetId, taskId } = useParams<TaskParams>();
  const location = useLocation();
  const isFromPlanning = location.pathname.includes('/planning/');
  const closeTo = isFromPlanning ? `/asset/${assetId}/planning` : `/asset/${assetId}/tasks`;
  const { data: tasks, isLoading } = useFetchTasks(assetId ?? '');
  const { mutate: updateTask } = useUpdateTask(assetId ?? '');
  const { mutate: deleteTask } = useDeleteTask(assetId ?? '');
  const task = tasks?.find(t => t.id === taskId);

  if (!taskId) {
    return <Navigate to={closeTo} replace />;
  }

  const handleStatusChange = (id: string, newStatus: TaskStatus) => {
    updateTask({
      taskId: id,
      data: { status: newStatus },
    });
  };

  return (
    <AnimatedRoutedDrawer title={t('tasks.details.title', 'Détail de la tâche')} to={closeTo}>
      {({ onClose }) => {
        const handleDelete = (id: string) => {
          deleteTask(id, { onSuccess: () => onClose() });
        };

        const handleEdit = () => {
          navigate('edit', { state: { disableAnimation: true }, replace: true });
        };

        if (isLoading) return <LoadingOverlay visible />;
        if (!task) return null;

        return (
          <TaskDetails
            task={task}
            editTask={handleEdit}
            deleteTask={handleDelete}
            onStatusChange={handleStatusChange}
          />
        );
      }}
    </AnimatedRoutedDrawer>
  );
};

export default ViewTaskDrawer;
